import { useMutation } from '@tanstack/react-query'
import { API_URL } from '@/config'
import type { RouterOutputs } from '@/trpc/client'

// Both the HTTP /api/ocr/extract endpoint and the tRPC items.extractFromReceipt
// procedure return the same OcrFlowResult shape from the server, so we derive
// the client types from the tRPC output — no hand-rolled duplicate to drift.
export type ExtractReceiptResult = RouterOutputs['items']['extractFromReceipt']
export type OcrResult = Extract<
	ExtractReceiptResult,
	{ status: 'completed' }
>['result']
export type OcrLineItem = OcrResult['lineItems'][number]

// Mirrors the tRPC error shape so isLimitReachedError() works against errors
// thrown from this fetch-based mutation, without it having to know two formats.
class ExtractReceiptError extends Error {
	data: {
		code:
			| 'FORBIDDEN'
			| 'NOT_FOUND'
			| 'TOO_MANY_REQUESTS'
			| 'INTERNAL_SERVER_ERROR'
	}
	constructor(message: string, code: ExtractReceiptError['data']['code']) {
		super(message)
		this.data = { code }
	}
}

async function extractReceipt(input: {
	file: File
	fileKey: string
}): Promise<ExtractReceiptResult> {
	const body = new FormData()
	body.append('fileKey', input.fileKey)
	body.append('file', input.file, input.file.name)

	const res = await fetch(`${API_URL}/api/ocr/extract`, {
		method: 'POST',
		credentials: 'include',
		// Custom header forces a CORS preflight on this credentialed
		// multipart POST. Server rejects requests that lack it.
		headers: { 'x-warrantly-csrf': '1' },
		body,
	})

	if (!res.ok) {
		const json = (await res.json().catch(() => ({}))) as {
			error?: string
		}
		const code = ((): ExtractReceiptError['data']['code'] => {
			switch (res.status) {
				case 401:
				case 403:
					return 'FORBIDDEN'
				case 404:
					return 'NOT_FOUND'
				case 429:
					return 'TOO_MANY_REQUESTS'
				default:
					return 'INTERNAL_SERVER_ERROR'
			}
		})()
		throw new ExtractReceiptError(json.error ?? 'OCR request failed', code)
	}

	return (await res.json()) as ExtractReceiptResult
}

export function useExtractReceipt(opts: {
	onSuccess?: (data: ExtractReceiptResult) => void
	onError?: (err: Error) => void
}) {
	return useMutation({
		mutationFn: extractReceipt,
		onSuccess: opts.onSuccess,
		onError: opts.onError,
	})
}
